import { AssetType, DailyRuntime } from '../types/types';
import { PUMP_RUNTIME_TAG_NAME } from '../config';
import { useWebSockets } from './useWebSockets';
import { useCallback, useEffect, useState } from 'react';
import { ReadingsService } from 'src/services';
import { getStartEndLastSevenDays } from '../utils';
import { format } from 'date-fns';

type usePumpRuntimeDataTypes = {
  customerId: string | null;
  siteId: string | null;
  asset: AssetType;
};

const formatRuntimeValue = (runtime: any) => {
  if (!runtime) return 0;

  const toNumber = Number(runtime.value);
  if (Number.isNaN(toNumber)) {
    return 0;
  }

  return Number((toNumber / 3600).toFixed(2));
};

export const usePumpRuntimeData = ({ customerId, siteId, asset }: usePumpRuntimeDataTypes) => {
  const [dailyRuntimes, setDailyRuntimes] = useState<DailyRuntime[] | null>(null);
  const assetTag = asset?.assignedTags?.find((tag) => tag?.tagName === PUMP_RUNTIME_TAG_NAME);
  const { messages } = useWebSockets(assetTag?.assignedGatewayId!);

  const latestMessage = messages[messages.length === 0 ? 0 : messages.length - 1];
  const gatewayInputs = latestMessage?.payload.data.onCreatedReading.gateway_inputs;
  const timestamp = latestMessage?.payload.data.onCreatedReading.timestamp;
  const findInput = gatewayInputs?.find(
    (input: { id: string | undefined }) => input.id === assetTag?.assignedTagId
  );
  const gatewayId = assetTag?.assignedGatewayId;

  const getRuntimeValues = useCallback(async () => {
    if (customerId && gatewayId && siteId) {
      const lastSevenDays = getStartEndLastSevenDays();
      let runtimeValues: DailyRuntime[] = [];
      for (let i = 0; i < lastSevenDays.length; i++) {
        const data = await ReadingsService.getAll({
          customerId,
          siteId,
          gatewayId,
          limit: 1,
          fromDate: lastSevenDays[i].dayStart,
          toDate: lastSevenDays[i].dayEnd,
        });
        const readings = data;
        if (readings.items.length !== 0) {
          const runtimeValue = readings.items[0].gatewayInputs.find(
            (item) => item.id === assetTag?.assignedTagId
          );
          runtimeValues.push({
            day: format(new Date(lastSevenDays[i].dayStart.replace('Z', '')), 'MM.dd'),
            value: formatRuntimeValue(runtimeValue),
          });
          continue;
        }
        runtimeValues.push({
          day: format(new Date(lastSevenDays[i].dayStart), 'MM.dd'),
          value: 0,
        });
      }
      setDailyRuntimes(runtimeValues);
    }
  }, [assetTag?.assignedTagId, customerId, siteId, gatewayId]);

  useEffect(() => {
    if (gatewayId && findInput && timestamp) {
      const date = format(new Date(timestamp * 1000), 'MM.dd');
      const runtimeValue = formatRuntimeValue(findInput);
      setDailyRuntimes((prev) => {
        if (prev === null) {
          return [{ day: date, value: runtimeValue }];
        }
        const runtimes = [...prev];
        runtimes[runtimes.length - 1] = { day: date, value: runtimeValue };
        return runtimes;
      });
    }
  }, [gatewayId, findInput, timestamp]);

  useEffect(() => {
    if (customerId && gatewayId) {
      setDailyRuntimes(null);
      getRuntimeValues();
      return;
    }
    setDailyRuntimes([]);
  }, [customerId, siteId, gatewayId, assetTag?.assignedTagId, getRuntimeValues]);

  //console.log('DAILY RUNTIMES', dailyRuntimes);

  return { dailyRuntimes };
};
